import DataQuery from "data/DataQuery";
import { useQuery } from "data/hooks/useQuery";
import ConditionalExecute from "data/models/ConditionalExecute";
import PagedDataResultDTO from "features/shared/dtos/PagedDataResultDTO";
import { getRouteParams } from "features/shared/helpers/routeParams";
import CustomerState from "infrastructure/store/customer/CustomerState";
import IAppState from "infrastructure/store/IAppState";
import React, { DependencyList, Fragment, useEffect } from "react";
import { useSelector } from "react-redux";
import NoContentWrapper from "features/shared/components/noContentWrapper/NoContentWrapper";
import { useInjection } from "infrastructure/ioc/ioc.react";
import IDataFilterHandler from "features/shared/components/dataFilter/IDataFilterHandler";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import { DataGridId } from "./DataGridId";
import { useState } from "react";
import IDataFilterDescriptor from "features/shared/components/dataFilter/IDataFilterDescriptor";
import Pagination from "features/shared/components/pagination/Pagination";
import DataFilterModal from "features/shared/components/dataFilterModal/DataFilterModal";
import DataGridHeader from "./DataGridHeader";
import IDefaultDataGridConfiguration from "./IDefaultDataGridConfiguration";
import DataGridRow from "./DataGridRow";
import IExternalFilterData from "features/shared/components/dataFilter/IExternalFilterData";
import IPaginationHandler from "features/shared/components/pagination/IPaginationHandler";
import { loadModule } from "features/shared/helpers/modules";
import SortingItemDescriptor from "features/shared/components/sorting/models/SortingItemDescriptor";
import { ISortingStateHandler } from "features/shared/components/sorting/handlers/ISortingStateHandler";
import ISortingStoreHandler from "features/shared/components/sorting/handlers/ISortingStoreHandler";
import SortingMenu from "features/shared/components/sorting/SortingMenu";
import { SortingDirection } from "features/shared/components/sorting/models/SortingDirection";
import { useDataFilterDefaults } from "../dataFilter/hooks/useDataFilterDefaults";

// defaults
const defaultGridConfiguration = loadModule<IDefaultDataGridConfiguration>("dataGrids/defaultDataGridConfiguration.json");

interface Props<T> {
  gridId: DataGridId;
  url: string;
  renderItem: (item: T) => JSX.Element;
  renderHeader: () => JSX.Element;
  filterJson?: string;
  filterExternalData?: IExternalFilterData<any>[];
  filterDefaultsUrl?: string;
  sortingJson?: string;
  renderGridHeaderBtns?: () => JSX.Element;
  gridConfig?: IDefaultDataGridConfiguration;
  itemAsCard?: boolean;
  renderNoContent?: () => JSX.Element;
  routeParams?: URLSearchParams;
  noFilterQueryParams?: URLSearchParams;
  externalDependencies?: DependencyList;
  renderCustomGridHeader?: (
    count: number,
    filterActive?: boolean,
    setShowFilterModal?: React.Dispatch<React.SetStateAction<boolean>>
  ) => JSX.Element;
}

const DataGrid = <T extends object>({
  gridId,
  url,
  renderItem,
  renderHeader,
  filterJson,
  filterExternalData,
  filterDefaultsUrl,
  sortingJson,
  gridConfig = defaultGridConfiguration,
  renderGridHeaderBtns,
  itemAsCard = true,
  renderNoContent,
  routeParams,
  noFilterQueryParams,
  externalDependencies,
  renderCustomGridHeader
}: Props<T>) => {
  // DI
  const _dataFilterHandler = useInjection<IDataFilterHandler>(IocTypes.DataFilterHandler);
  const _paginationHandler = useInjection<IPaginationHandler>(IocTypes.PaginationHandler);
  const _sortingStateHandler = useInjection<ISortingStateHandler>(IocTypes.SortingStateHandler);
  const _sortingStoreHandler = useInjection<ISortingStoreHandler>(IocTypes.SortingStoreHandler);

  // STORE
  const customer = useSelector<IAppState, CustomerState>(
    (store) => store.customer
  ).customer;

  const filterUrlSearchParams = useSelector<IAppState, string | undefined>(
    (state) => state.dataGrid.gridSettings[gridId]?.filtrUrlSearchParams
  );

  const paginationUrlSearchParams = useSelector<IAppState, string>(
    (state) => state.dataGrid.gridSettings[gridId]?.paginationUrlSearchParams
  );

  const sortingUrlSearchParams = useSelector<IAppState, string | undefined>(
    (state) => state.dataGrid.gridSettings[gridId]?.sortingUrlSearchParams
  );

  const computedQueryParams = useSelector<IAppState, string>(
    (state) =>
      state.dataGrid.gridSettings[gridId]?.computedUrlSearchParams ?? ""
  );

  // STATE
  const [showFilter, setShowFilter] = useState<boolean>(false);
  const [descriptors, setDescriptors] = useState<IDataFilterDescriptor[]>([]);
  const [sortingDescriptors, setSortingDescriptors] = useState<SortingItemDescriptor[]>(sortingJson ? _sortingStateHandler.loadSortingDescriptors(sortingJson) : []);

  // SIDE-EFFECTS
  // set filter defaults (only if filterDefaultsUrl is not empty)
  const filterDefaultsLoadStatus = useDataFilterDefaults(gridId, filterDefaultsUrl ?? "", filterDefaultsUrl ? true : false);

  // fetch data
  const getQueryParams = (): URLSearchParams => {
    if (noFilterQueryParams) {
      let mergeQueryParams = new URLSearchParams(computedQueryParams);
      noFilterQueryParams.forEach((value, key) => {
        mergeQueryParams.append(key, value);
      });

      return mergeQueryParams;
    }

    return new URLSearchParams(computedQueryParams);
  }

  const canExecuteQuery = (): boolean => { 
    // if is set filterDefaultsUrl wait to load filter defaults
    if (filterDefaultsUrl) {
      if (filterDefaultsLoadStatus == "AfterFirstLoad") {
        return computedQueryParams ? true : false;
      }
      return false;
    } else {
      // execute query (load data) only if computedQueryParams is not empty
      return computedQueryParams ? true : false;
    }
  }

  const dataQuery: DataQuery = {
    url: url,
    routeParams: routeParams ?? getRouteParams("customerId", customer.customerId!),
    params: getQueryParams()
  };
  const execute: ConditionalExecute = {
    apply: true,
    condition: canExecuteQuery
  };
  const { data, loadStatus } = useQuery<PagedDataResultDTO<T>>(
    dataQuery,
    { result: [], count: 0 },
    [
      ...(externalDependencies ?? []),
      customer,
      computedQueryParams,
      routeParams,
      filterDefaultsLoadStatus,
    ],
    { useTracker: true },
    execute
  );

  // setting handler for filter
  useEffect(() => {
    if (!filterJson) {
      // if filtering not enabled, ensure descriptors are cleared
      if (descriptors.length) setDescriptors([]);
      return;
    }
    const desc = _dataFilterHandler.processFilter(
      filterJson,
      new URLSearchParams(filterUrlSearchParams)
    );
    setDescriptors(desc);
  }, [filterJson, filterUrlSearchParams]);

  // setting handler for pagination
  useEffect(() => {
    _paginationHandler.storeSetDefaultParams(
      gridId,
      new URLSearchParams(paginationUrlSearchParams),
      1,
      gridConfig.pageSize
    );
  }, []);
  // set default sorting
  useEffect(() => {
    if (sortingJson) {
      // active sorting params from state (JSON)
      const activeSortingParamsFromState = _sortingStateHandler.getUrlParamsOfActiveSorting(sortingDescriptors);
      // set sorting params to store
      _sortingStoreHandler.storeSetDefaultSorting(gridId, sortingUrlSearchParams ?? "", activeSortingParamsFromState);
      // change sorting in state (if exists sortingUrlSearchParams (= previous sorting params))
      _sortingStateHandler.changeSortingByUrlParams(sortingUrlSearchParams ?? "", sortingDescriptors, setSortingDescriptors);
    }
  }, []);

  // HANDLERS
  const paginationChangePageHandler = (pageNumber: number) => {
    _paginationHandler.storeSetPagination(
      gridId,
      new URLSearchParams(paginationUrlSearchParams),
      pageNumber
    );
  };

  const filterApplyHandler = () => {
    let params = new URLSearchParams();
    _dataFilterHandler.setDescriptorValuesToUrlParams(descriptors, params);

    _dataFilterHandler.storeDataGridSettings(gridId, params);
    setShowFilter(false);
  };

  const filterResetHandler = () => {
    _dataFilterHandler.storeResetDataGridSettings(gridId);

    setShowFilter(false);
  };

  const sortingChangeHandler = (name: string, direction: SortingDirection) => { 
    // sorting change (in state)
    _sortingStateHandler.changeSorting(name, direction, sortingDescriptors, setSortingDescriptors);
    // set new sorting params to store
    const sortingUrlSearchParams = _sortingStateHandler.getUrlParamsOfActiveSorting(sortingDescriptors);
    _sortingStoreHandler.storeChangeSorting(gridId, sortingUrlSearchParams);
  }

  // HELPERS
  const filterActive = (): boolean => {
    if (!filterJson) return false;
    return filterUrlSearchParams !== "" && filterUrlSearchParams !== undefined;
  };

  // SUB-RENDERS
  const renderItems = () => {
    return data.result.map(renderItemRow);
  };

  const renderItemRow = (item: T, index: number) => {
    if (itemAsCard) {
      return <DataGridRow key={index}>{renderItem(item)}</DataGridRow>;
    } else {
      return <Fragment key={index}>{renderItem(item)}</Fragment>;
    }
  };

  const renderDataGridHeader = () => {
    if (renderCustomGridHeader) {
      return renderCustomGridHeader(
        data.count,
        filterActive(),
        filterJson ? setShowFilter : undefined
      );
    } else {
      return (
        <DataGridHeader
          setShowFilterModal={filterJson ? setShowFilter : undefined}
          count={data.count}
          actionBtns={renderGridHeaderBtns}
          filterActive={filterJson ? filterActive() : undefined}
          renderSorting={sortingJson ? renderSortingMenu : undefined}
        />
      );
    }
  };

  const renderPagination = () => {
    return (
      <div className="d-flex justify-content-center mt-4">
        <Pagination
          count={data.count}
          pageSize={gridConfig.pageSize}
          urlParams={new URLSearchParams(paginationUrlSearchParams)}
          onPageChange={paginationChangePageHandler}
        />
      </div>
    );
  };

  const renderFilter = () => {
    if (!filterJson) return <></>; // filtering disabled
    return (
      <DataFilterModal
        descriptors={descriptors}
        setDescriptors={setDescriptors}
        externalData={filterExternalData}
        applyButtonOnClick={filterApplyHandler}
        resetButtonOnClick={filterResetHandler}
        show={showFilter}
        setShow={setShowFilter}
      />
    );
  };

  const renderSortingMenu = () => {
    if (sortingJson) {
      return (
        <SortingMenu 
          id="invoices-sorting-menu"
          onSortingClick={sortingChangeHandler}
          descriptors={sortingDescriptors}
        />
      );
    }
    
    return <></>;
  }

  // RENDER
  return (
    <>
      <NoContentWrapper
        data={data}
        loadStatus={loadStatus}
        noContentAsAlert
        gridId={gridId}
        noContentRender={renderNoContent}
      >
        {renderDataGridHeader()}
        {renderHeader()}
        {renderItems()}
        {renderFilter()}
        {renderPagination()}
      </NoContentWrapper>
    </>
  );
};

export default DataGrid;
